import React, { useState } from 'react';
import { Shield, CheckCircle, ArrowRight, User, Mail, Phone, Globe } from 'lucide-react';

interface AdvisorContactFormProps {
    serviceType: string;
    onSuccess?: () => void;
    onCancel?: () => void;
    initialData?: any;
}

const AdvisorContactForm: React.FC<AdvisorContactFormProps> = ({ serviceType, onSuccess, onCancel, initialData }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('+44');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();
        formData.append('service', serviceType);
        formData.append('name', name);
        formData.append('email', email);
        formData.append('phone', countryCode + phone);
        
        // Add any supplementary AI data if provided
        if (initialData) {
            Object.keys(initialData).forEach(key => {
                formData.append(key, initialData[key]);
            });
        }

        try {
            const response = await fetch('/api/eligibility', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                setSubmitted(true);
                if (onSuccess) setTimeout(onSuccess, 3000);
            }
        } catch (error) {
            console.error("Submission failed", error);
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center animate-fadeIn">
                <div className="w-20 h-20 bg-[#009B8F]/10 text-[#009B8F] rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={40} />
                </div>
                <h3 className="text-2xl font-serif text-[#011E26] mb-2">Request Received</h3>
                <p className="text-slate-600 mb-6">
                    A senior advisor has been assigned to your profile. We will contact you shortly to discuss your strategy.
                </p>
                <button 
                    onClick={onCancel}
                    className="text-[#009B8F] font-bold uppercase tracking-widest text-xs hover:underline"
                >
                    Return to Checker
                </button>
            </div>
        );
    }

    return (
        <div className="bg-slate-50 p-6 md:p-8 rounded-xl border border-slate-200 shadow-inner animate-fadeIn text-[#011E26]">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#011E26] rounded flex items-center justify-center text-[#009B8F]">
                    <Shield size={20} />
                </div>
                <div>
                    <h4 className="text-lg font-bold text-[#011E26]">Speak to an Advisor</h4>
                    <p className="text-xs text-slate-500 uppercase tracking-widest">Expert Document Review & Strategy</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                        required
                        type="text"
                        placeholder="Full Name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        className="w-full p-3 pl-10 bg-white border border-gray-200 rounded text-sm focus:outline-none focus:border-[#009B8F] transition-all"
                    />
                </div>

                <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                        required
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full p-3 pl-10 bg-white border border-gray-200 rounded text-sm focus:outline-none focus:border-[#009B8F] transition-all"
                    />
                </div>

                <div className="flex gap-2">
                    <div className="relative w-24">
                        <Globe className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <select
                            value={countryCode}
                            onChange={e => setCountryCode(e.target.value)}
                            className="w-full p-3 pl-7 bg-white border border-gray-200 rounded text-sm appearance-none focus:outline-none focus:border-[#009B8F] transition-all cursor-pointer"
                        >
                            <option value="+44">+44</option>
                            <option value="+1">+1</option>
                            <option value="+91">+91</option>
                            <option value="+971">+971</option>
                            <option value="+86">+86</option>
                        </select>
                    </div>
                    <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            required
                            type="tel"
                            placeholder="Phone Number"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            className="w-full p-3 pl-10 bg-white border border-gray-200 rounded text-sm focus:outline-none focus:border-[#009B8F] transition-all"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-[#011E26] hover:bg-[#009B8F] text-white font-bold text-sm uppercase tracking-[0.2em] rounded transition-all flex items-center justify-center gap-3 group mt-2"
                >
                    {loading ? 'Processing...' : 'Request Professional Review'}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
            </form>

            <button
                type="button"
                onClick={onCancel}
                className="w-full text-center text-[10px] text-slate-400 mt-6 hover:text-slate-600 font-bold uppercase tracking-widest"
            >
                Cancel and view results
            </button>
        </div>
    );
};

export default AdvisorContactForm;
